import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../../contexts/ThemeContext';
import { COLORS } from '../../../../constants/booking';

interface VehicleStats {
  total: number;
  available: number;
  onDuty: number;
  offDuty: number;
  maintenance: number;
  inTrip: number;
}

interface VehicleSummaryCardsProps {
  vehicleStats: VehicleStats;
  zoneAllocation?: { areasWithAvailable: number; totalAreas: number };
  onFilterChange?: (filterCategory: string) => void;
  selectedFilter?: string;
}

const VehicleSummaryCards: React.FC<VehicleSummaryCardsProps> = ({ vehicleStats, zoneAllocation, onFilterChange, selectedFilter }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  // Viewport-based layout: wrap cards ONLY on very small screens (<= 430px)
  const [isVeryNarrow, setIsVeryNarrow] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsVeryNarrow(window.innerWidth <= 430);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to create donut chart SVG
  const createDonutChart = (percentage: number, color: string, size: number = 40) => {
    const strokeWidth = 8;
    const radius = size / 2 - strokeWidth / 2 - 1;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;
    
    return (
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={isDark ? '#334155' : '#e2e8f0'}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
    );
  };

  // Calculate inactive (off duty + maintenance)
  const inactive = vehicleStats.offDuty + vehicleStats.maintenance;

  // Status card data
  const statusCards = [
    {
      label: 'Total Vehicle',
      value: vehicleStats.total,
      color: '#3b82f6',
      percentage: 100,
      isTotal: true,
      filterCategory: 'total',
    },
    {
      label: 'Standby',
      value: vehicleStats.available,
      color: '#10b981',
      percentage: vehicleStats.total > 0 ? (vehicleStats.available / vehicleStats.total) * 100 : 0,
      filterCategory: 'standby',
    },
    {
      label: 'On Trip',
      value: vehicleStats.onDuty,
      color: '#ef4444',
      percentage: vehicleStats.total > 0 ? (vehicleStats.onDuty / vehicleStats.total) * 100 : 0,
      filterCategory: 'on_trip',
    },
    {
      label: 'Inactive',
      value: inactive,
      color: '#64748b',
      percentage: vehicleStats.total > 0 ? (inactive / vehicleStats.total) * 100 : 0,
      filterCategory: 'inactive',
    },
    {
      label: 'Zone Allotted',
      value: zoneAllocation && zoneAllocation.totalAreas > 0 
        ? `${zoneAllocation.areasWithAvailable}/${zoneAllocation.totalAreas}`
        : '0/0',
      color: '#8b5cf6',
      percentage: zoneAllocation && zoneAllocation.totalAreas > 0
        ? (zoneAllocation.areasWithAvailable / zoneAllocation.totalAreas) * 100
        : 0,
      filterCategory: 'zone_allocation',
      isSelectable: false,
      onClick: () => {
        navigate('/dashboard/ambulances?tab=areaMapping');
      },
    },
  ];

  return (
    <div
      className="summary-cards-container"
      style={{
        display: 'flex',
        flexDirection: isVeryNarrow ? 'column' : 'row',
        gap: '8px',
        flexWrap: 'wrap',
        marginTop: '16px',
        overflow: 'visible',
      }}
    >
      {statusCards.map((card) => {
        const isSelectable = card.isSelectable !== false; // Default to true if not specified
        const isSelected = isSelectable && selectedFilter === card.filterCategory;
        const handleClick = () => {
          // If card has custom onClick (like Zone Allocation), use it
          if (card.onClick) {
            card.onClick();
            return;
          }
          
          if (!onFilterChange || !isSelectable) return;
          
          // If clicking on Total, always select it
          if (card.filterCategory === 'total') {
            onFilterChange('total');
            return;
          }
          
          // If clicking on Standby, On Trip, or Inactive:
          // - If already selected, deselect and select Total
          // - If not selected, select it
          if (isSelected) {
            // Second click: deselect and select Total
            onFilterChange('total');
          } else {
            // First click: select this filter
            onFilterChange(card.filterCategory || 'total');
          }
        };
        
        return (
        <div
          key={card.label}
          className="summary-card"
          onClick={card.onClick || (isSelectable ? handleClick : undefined)}
          style={{
            flex: isVeryNarrow ? '0 0 auto' : '1 1 0',
            width: isVeryNarrow ? '100%' : undefined,
            minWidth: isVeryNarrow ? undefined : 0,
            backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
            borderRadius: '6px',
            padding: '8px 0px',
            border: `2px solid ${isSelected ? card.color : (isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER)}`,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            cursor: (card.onClick || isSelectable) ? 'pointer' : 'default',
            transition: 'border-color 0.2s, transform 0.1s',
            transform: isSelected ? 'scale(1.02)' : 'scale(1)',
          }}
          onMouseEnter={(card.onClick || isSelectable) ? (e) => {
            if (!isSelected) {
              e.currentTarget.style.borderColor = card.color;
              e.currentTarget.style.opacity = '0.9';
            }
          } : undefined}
          onMouseLeave={(card.onClick || isSelectable) ? (e) => {
            if (!isSelected) {
              e.currentTarget.style.borderColor = isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER;
              e.currentTarget.style.opacity = '1';
            }
          } : undefined}
        >
          {/* Part 1: Pie Chart - 20% */}
          <div style={{ width: '20%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {createDonutChart(card.percentage, card.color, 36)}
          </div>
          
          {/* Part 2: Text - 60% */}
          <div style={{ width: '40%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div
              style={{
                fontSize: 'clamp(11px, 1.1vw, 14px)',
                fontWeight: '600',
                color: card.color,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {card.label}
            </div>
          </div>
          
          {/* Part 3: Number - 20% */}
          <div style={{ width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div
              style={{
                fontSize: 'clamp(18px, 2vw, 26px)',
                fontWeight: '700',
                color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                lineHeight: '1',
              }}
            >
              {card.value}
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default VehicleSummaryCards;

